/**
 * Zustand store for locations tree by project.
 * Hydrates from IndexedDB when available, then fetches from API if needed.
 * Persists to IndexedDB after fetch so refresh shows location names without a network call.
 * Also holds in-transit AI review data (location hierarchy + tasks + removed ids) until user confirms.
 */

import { create } from 'zustand';
import type { LocationChatMessage, LocationTreeNode } from '../api/schedulingApis';
import { fetchLocationsTreeByProject } from '../api/schedulingApis';
import { loadLocationTreeFromDB, saveLocationTreeToDB, clearLocationTreeForProject } from './db';
import { collectDescendantIds, filterTreeRemoved, moveNodesInTree, moveSiblingsWithMerge, updateNodeInTree, updateNodesInTreeByName } from '../location-hierarchy/aiReviewUtils';
import { buildCloneSubtree } from '../location-hierarchy/locationDraftUtils';
import {
  isFloorLocationType,
  isProjectLocationType,
  nextFloorIndexUnderParent,
  withFloorNumberMeta,
} from '../features/schedule-locations/logic/locationTypeRules';

function findNodeById(nodes: LocationTreeNode[], id: string): LocationTreeNode | null {
  for (const n of nodes) {
    if (String(n.location_id) === String(id)) return n;
    const inChild = findNodeById(n.child ?? [], id);
    if (inChild) return inChild;
  }
  return null;
}

export function insertNodeIntoTree(
  roots: LocationTreeNode[],
  parentId: string | null,
  newNode: LocationTreeNode
): LocationTreeNode[] {
  const clone: LocationTreeNode[] = JSON.parse(JSON.stringify(roots));
  if (parentId == null) {
    clone.push(newNode);
    return clone;
  }

  const targetId = String(parentId);
  let inserted = false;
  const walk = (nodes: LocationTreeNode[]) => {
    for (const n of nodes) {
      if (String(n.location_id) === targetId) {
        n.child = Array.isArray(n.child) ? n.child : [];
        n.child.push(newNode);
        inserted = true;
        return;
      }
      if (n.child?.length) {
        walk(n.child);
        if (inserted) return;
      }
    }
  };
  walk(clone);
  return inserted ? clone : roots;
}

/** Name key for merge comparisons: case and internal whitespace are not meaningful here. */
function locationNameKey(name: string | null | undefined): string {
  return (name ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}

/** Re-point a grafted subtree at its new parent and depth. */
function reparentSubtree(
  node: LocationTreeNode,
  parentId: string | null,
  level: number,
): LocationTreeNode {
  return {
    ...node,
    parent_location_id: parentId,
    location_level: level,
    child: (node.child ?? []).map((c) => reparentSubtree(c, String(node.location_id), level + 1)),
  };
}

/**
 * Fold `incoming` into `existing`, matching siblings by name at every level.
 *
 * A branch whose name is already present is merged into that branch rather than added beside it,
 * recursively - so re-running classification over a hierarchy it already produced adds nothing.
 * Only genuinely new names are grafted, and those keep their whole subtree.
 */
function mergeBranchesByName(
  existing: LocationTreeNode[],
  incoming: LocationTreeNode[],
  parentId: string | null,
  level: number,
): LocationTreeNode[] {
  const out = existing.map((n) => ({ ...n }));
  const byName = new Map(out.map((n) => [locationNameKey(n.name), n]));
  for (const node of incoming) {
    const match = byName.get(locationNameKey(node.name));
    if (match) {
      match.child = mergeBranchesByName(
        match.child ?? [],
        node.child ?? [],
        String(match.location_id),
        (match.location_level ?? level) + 1,
      );
    } else {
      const grafted = reparentSubtree(node, parentId, level);
      out.push(grafted);
      byName.set(locationNameKey(node.name), grafted);
    }
  }
  return out;
}

export interface LocationDraftState {
  locationHierarchy: LocationTreeNode[];
  removedIds: string[];
  /**
   * True when this draft came from task classification rather than hand edits. Saving it routes
   * through reassignLocationToTask so tasks are re-matched to the saved locations, instead of the
   * plain location-only update.
   */
  fromClassification?: boolean;
}

interface LocationDraftHistory {
  past: LocationDraftState[];
  future: LocationDraftState[];
  initial: LocationDraftState | null;
}

function ensureLocationDraftFromState(
  s: LocationsTreeState,
  projectId: string,
): { draft: LocationDraftState; history: LocationDraftHistory } | null {
  const cached = s.treeByProjectId[projectId];
  if (!cached?.length) return null;
  const existing = s.locationDraftByProjectId[projectId];
  if (existing) {
    const history = s.locationDraftHistoryByProjectId[projectId] ?? {
      past: [],
      future: [],
      initial: JSON.parse(JSON.stringify(existing)),
    };
    return { draft: existing, history };
  }
  const initial: LocationDraftState = {
    locationHierarchy: JSON.parse(JSON.stringify(cached)),
    removedIds: [],
  };
  const history: LocationDraftHistory = {
    past: [],
    future: [],
    initial: JSON.parse(JSON.stringify(initial)),
  };
  return { draft: initial, history };
}

function pushDraftHistory(
  history: LocationDraftHistory,
  current: LocationDraftState,
): LocationDraftHistory {
  const snapshot: LocationDraftState = JSON.parse(JSON.stringify(current));
  return {
    ...history,
    past: [...history.past, snapshot].slice(-20),
    future: [],
  };
}

/**
 * A single draft mutation, produced by the location chat planner and applied as one batch so the
 * whole confirmed change set costs exactly one undo step.
 */
export type LocationDraftOp =
  | { kind: 'add'; parentId: string | null; node: LocationTreeNode }
  | {
      kind: 'update';
      nodeId: string;
      updates: Partial<Pick<LocationTreeNode, 'location_type' | 'name' | 'location_group' | 'meta'>>;
    }
  | { kind: 'move'; nodeId: string; parentId: string | null; index: number }
  | { kind: 'remove'; ids: string[] };

export interface LocationsTreeState {
  /** Cached tree data by projectId */
  treeByProjectId: Record<string, LocationTreeNode[]>;
  /** Project id currently loading (at most one in-flight) */
  loadingProjectId: string | null;
  /** In-flight promise by projectId so concurrent callers await the same request */
  fetchPromiseByProjectId: Record<string, Promise<LocationTreeNode[]>>;
  /** Last error message by projectId */
  errorByProjectId: Record<string, string | null>;
  /** In-memory location edit draft per project (normal management, batch save). */
  locationDraftByProjectId: Record<string, LocationDraftState | null>;
  /** Undo/redo history for location draft per project (bounded). */
  locationDraftHistoryByProjectId: Record<string, LocationDraftHistory | undefined>;
  /**
   * Location assistant transcript per project.
   *
   * Lives here rather than in the page so the thread survives navigating away and back - it is
   * the only record of a classification run that took a minute. In memory only: a reload still
   * starts fresh, which is the intended lifetime for scratch conversation.
   */
  locationChatByProjectId: Record<string, LocationChatMessage[]>;
}

export interface LocationsTreeActions {
  /** Get cached tree for project, or null if not loaded */
  getTree: (projectId: string) => LocationTreeNode[] | null;
  /** Fetch tree for project if not already cached; stores result for reuse */
  fetchTreeIfNeeded: (projectId: string) => Promise<LocationTreeNode[]>;
  /** Clear cached tree for a project (in-memory and IndexedDB) so next fetch hits API. */
  clearTree: (projectId: string) => Promise<void>;
  /** Get location draft for project, or null. */
  getLocationDraft: (projectId: string) => LocationDraftState | null;
  /** Get effective location draft tree (hierarchy minus removed nodes). Null if no draft. */
  getLocationDraftEffectiveTree: (projectId: string) => LocationTreeNode[] | null;
  /** Clear location draft for project. */
  clearLocationDraft: (projectId: string) => void;
  /**
   * Replace the draft with a classified hierarchy, or fold it into what is already there.
   *
   * "replace" marks every existing location removed, so Save deletes them; "merge" appends the
   * branches whose names are not already present at the top level. Either way it is one undo step
   * and nothing is written until Save.
   */
  loadClassificationIntoDraft: (
    projectId: string,
    locationHierarchy: LocationTreeNode[],
    mode: 'replace' | 'merge',
  ) => void;
  /** Bulk move selected sibling nodes under a new parent in the draft, merging by name+type. */
  moveLocationDraftSiblingsWithMerge: (
    projectId: string,
    selectedIds: string[],
    targetParentId: string | null,
  ) => void;
  /** Mark node ids (and their descendants) as removed in location draft. */
  addLocationDraftRemovedIds: (projectId: string, ids: string[]) => void;
  /** Move nodes in location draft tree (for drag-and-drop). */
  moveLocationDraftNode: (projectId: string, dragIds: string[], parentId: string | null, index: number) => void;
  /** Add a node under parent in location draft tree. */
  addLocationDraftNode: (projectId: string, parentId: string | null, node: LocationTreeNode) => void;
  /** Update a node in location draft tree. */
  updateLocationDraftNode: (
    projectId: string,
    nodeId: string,
    updates: Partial<Pick<LocationTreeNode, 'location_type' | 'name' | 'location_group' | 'meta'>>,
  ) => void;
  /** Update all nodes in location draft with the given name (case-sensitive). */
  updateLocationDraftNodesByName: (
    projectId: string,
    name: string,
    updates: Partial<Pick<LocationTreeNode, 'location_type'>>,
  ) => void;
  /** Clone a node (and subtree) in location draft tree. */
  cloneLocationDraftNode: (projectId: string, nodeId: string) => void;
  /** Paste a copied subtree as a child of parentId in location draft tree. */
  pasteLocationDraftSubtree: (projectId: string, parentId: string, sourceSubtree: LocationTreeNode) => LocationTreeNode | null;
  /**
   * Apply a batch of draft ops in one shot. Unlike calling the single-node actions in a loop this
   * pushes exactly one history entry, so a 12-floor chat confirmation is one Ctrl+Z.
   */
  applyLocationDraftOps: (projectId: string, ops: LocationDraftOp[]) => void;
  /** Undo last location draft step for a project, if any. */
  undoLocationDraftStep: (projectId: string) => void;
  /** Redo last undone location draft step for a project, if any. */
  redoLocationDraftStep: (projectId: string) => void;
  /** Reset location draft back to the initial snapshot and clear history. */
  resetLocationDraftToInitial: (projectId: string) => void;
  /** Replace the assistant transcript for a project. */
  setLocationChat: (projectId: string, messages: LocationChatMessage[]) => void;
}

export type LocationsTreeStore = LocationsTreeState & LocationsTreeActions;

export const useLocationsTreeStore = create<LocationsTreeStore>((set, get) => ({
  treeByProjectId: {},
  loadingProjectId: null,
  fetchPromiseByProjectId: {},
  errorByProjectId: {},
  locationDraftByProjectId: {},
  locationDraftHistoryByProjectId: {},
  locationChatByProjectId: {},

  getTree(projectId: string) {
    return get().treeByProjectId[projectId] ?? null;
  },

  async fetchTreeIfNeeded(projectId: string): Promise<LocationTreeNode[]> {
    const cached = get().treeByProjectId[projectId];
    if (cached != null) {
      return cached;
    }
    const inFlight = get().fetchPromiseByProjectId[projectId];
    if (inFlight) {
      return inFlight;
    }
    const promise = (async () => {
      set((s) => ({
        loadingProjectId: projectId,
        errorByProjectId: { ...s.errorByProjectId, [projectId]: null },
      }));
      try {
        const fromDb = await loadLocationTreeFromDB(projectId);
        if (fromDb != null && fromDb.length > 0) {
          set((s) => {
            const nextPromise = { ...s.fetchPromiseByProjectId };
            delete nextPromise[projectId];
            return {
              treeByProjectId: { ...s.treeByProjectId, [projectId]: fromDb },
              loadingProjectId: s.loadingProjectId === projectId ? null : s.loadingProjectId,
              fetchPromiseByProjectId: nextPromise,
              errorByProjectId: { ...s.errorByProjectId, [projectId]: null },
            };
          });
          return fromDb;
        }
        const res = await fetchLocationsTreeByProject({ projectId });
        const data = res.data ?? [];
        await saveLocationTreeToDB(projectId, data);
        set((s) => {
          const next = { ...s.fetchPromiseByProjectId };
          delete next[projectId];
          return {
            treeByProjectId: { ...s.treeByProjectId, [projectId]: data },
            loadingProjectId: s.loadingProjectId === projectId ? null : s.loadingProjectId,
            fetchPromiseByProjectId: next,
            errorByProjectId: { ...s.errorByProjectId, [projectId]: null },
          };
        });
        return data;
      } catch (err) {
        set((s) => {
          const next = { ...s.fetchPromiseByProjectId };
          delete next[projectId];
          return {
            loadingProjectId: s.loadingProjectId === projectId ? null : s.loadingProjectId,
            fetchPromiseByProjectId: next,
            errorByProjectId: {
              ...s.errorByProjectId,
              [projectId]: err instanceof Error ? err.message : 'Failed to load location hierarchy',
            },
          };
        });
        throw err;
      }
    })();
    set((s) => ({
      fetchPromiseByProjectId: { ...s.fetchPromiseByProjectId, [projectId]: promise },
    }));
    return promise;
  },

  async clearTree(projectId: string): Promise<void> {
    await clearLocationTreeForProject(projectId);
    set((s) => {
      const next = { ...s.treeByProjectId };
      delete next[projectId];
      return {
        treeByProjectId: next,
        errorByProjectId: { ...s.errorByProjectId, [projectId]: null },
      };
    });
  },

  getLocationDraft(projectId: string) {
    return get().locationDraftByProjectId[projectId] ?? null;
  },

  getLocationDraftEffectiveTree(projectId: string): LocationTreeNode[] | null {
    const state = get().locationDraftByProjectId[projectId];
    if (!state?.locationHierarchy?.length) return null;
    const removedSet = new Set(state.removedIds);
    return filterTreeRemoved(state.locationHierarchy, removedSet);
  },

  clearLocationDraft(projectId: string) {
    set((s) => {
      const next = { ...s.locationDraftByProjectId };
      delete next[projectId];
      const nextHistory = { ...(s.locationDraftHistoryByProjectId ?? {}) };
      delete nextHistory[projectId];
      return { locationDraftByProjectId: next, locationDraftHistoryByProjectId: nextHistory };
    });
  },

  addLocationDraftRemovedIds(projectId: string, ids: string[]) {
    set((s) => {
      const ensured = ensureLocationDraftFromState(s, projectId);
      if (!ensured) return s;
      const { draft: current, history } = ensured;
      const nextHistory = pushDraftHistory(history, current);
      const nextSet = new Set(current.removedIds);
      ids.forEach((id) => nextSet.add(id));
      const nextDraft: LocationDraftState = { ...current, removedIds: Array.from(nextSet) };
      return {
        locationDraftByProjectId: { ...s.locationDraftByProjectId, [projectId]: nextDraft },
        locationDraftHistoryByProjectId: {
          ...s.locationDraftHistoryByProjectId,
          [projectId]: nextHistory,
        },
      };
    });
  },

  moveLocationDraftNode(projectId: string, dragIds: string[], parentId: string | null, index: number) {
    set((s) => {
      const ensured = ensureLocationDraftFromState(s, projectId);
      if (!ensured?.draft.locationHierarchy?.length) return s;
      const { draft: current, history } = ensured;
      const nextHistory = pushDraftHistory(history, current);
      const nextTree = moveNodesInTree(current.locationHierarchy, dragIds, parentId, index);
      return {
        locationDraftByProjectId: {
          ...s.locationDraftByProjectId,
          [projectId]: { ...current, locationHierarchy: nextTree },
        },
        locationDraftHistoryByProjectId: {
          ...s.locationDraftHistoryByProjectId,
          [projectId]: nextHistory,
        },
      };
    });
  },

  setLocationChat(projectId: string, messages: LocationChatMessage[]) {
    set((s) => ({
      locationChatByProjectId: { ...s.locationChatByProjectId, [projectId]: messages },
    }));
  },

  loadClassificationIntoDraft(
    projectId: string,
    locationHierarchy: LocationTreeNode[],
    mode: 'replace' | 'merge',
  ) {
    set((s) => {
      if (!locationHierarchy?.length) return s;
      const ensured = ensureLocationDraftFromState(s, projectId);
      // Deep copy: the merge below rewrites branches in place, and the draft in the store must not
      // change until the new state is returned (the history snapshot is taken from it).
      const current: LocationDraftState = ensured?.draft
        ? JSON.parse(JSON.stringify(ensured.draft))
        : { locationHierarchy: [], removedIds: [] };
      const history = ensured?.history ?? {
        past: [],
        future: [],
        initial: JSON.parse(JSON.stringify(current)),
      };
      const nextHistory = pushDraftHistory(history, current);
      const derived: LocationTreeNode[] = JSON.parse(JSON.stringify(locationHierarchy));

      let nextTree: LocationTreeNode[];
      let removedIds: string[];
      if (mode === 'replace') {
        // Everything currently saved goes; Save turns removedIds into deletes.
        nextTree = derived;
        removedIds = Array.from(collectDescendantIds(current.locationHierarchy));
      } else {
        // Graft the derived branches under the project root that is already there, folding any
        // whose name already exists instead of adding a second copy. Both halves matter: the
        // derived tree carries its own project root, so appending its children to the top level
        // would put blocks beside the root rather than inside it, and comparing their names
        // against the root's own name would match nothing and duplicate every branch.
        const existingRoot = current.locationHierarchy.find((n) => isProjectLocationType(n.location_type));
        const derivedRoot = derived.find((n) => isProjectLocationType(n.location_type));
        const incoming = derivedRoot ? derivedRoot.child ?? [] : derived;

        if (existingRoot) {
          existingRoot.child = mergeBranchesByName(
            existingRoot.child ?? [],
            incoming,
            String(existingRoot.location_id),
            (existingRoot.location_level ?? 1) + 1,
          );
          nextTree = current.locationHierarchy;
        } else {
          nextTree = mergeBranchesByName(current.locationHierarchy, incoming, null, 1);
        }
        removedIds = current.removedIds;
      }

      return {
        locationDraftByProjectId: {
          ...s.locationDraftByProjectId,
          [projectId]: { locationHierarchy: nextTree, removedIds, fromClassification: true },
        },
        locationDraftHistoryByProjectId: {
          ...s.locationDraftHistoryByProjectId,
          [projectId]: nextHistory,
        },
      };
    });
  },

  moveLocationDraftSiblingsWithMerge(
    projectId: string,
    selectedIds: string[],
    targetParentId: string | null,
  ) {
    set((s) => {
      const ensured = ensureLocationDraftFromState(s, projectId);
      if (!ensured?.draft.locationHierarchy?.length) return s;
      const { draft: current, history } = ensured;
      const { tree, idMapping, mergedAwayIds } = moveSiblingsWithMerge(
        current.locationHierarchy,
        selectedIds,
        targetParentId,
      );
      if (Object.keys(idMapping).length === 0) return s;
      const nextHistory = pushDraftHistory(history, current);
      return {
        locationDraftByProjectId: {
          ...s.locationDraftByProjectId,
          [projectId]: {
            ...current,
            locationHierarchy: tree,
            // Merged-away nodes are real saved rows here. Without this they would vanish from the
            // tree but survive in the database, orphaned.
            removedIds: Array.from(new Set([...current.removedIds, ...mergedAwayIds])),
          },
        },
        locationDraftHistoryByProjectId: {
          ...s.locationDraftHistoryByProjectId,
          [projectId]: nextHistory,
        },
      };
    });
  },

  addLocationDraftNode(projectId: string, parentId: string | null, node: LocationTreeNode) {
    set((s) => {
      const ensured = ensureLocationDraftFromState(s, projectId);
      if (!ensured?.draft.locationHierarchy?.length) return s;
      const { draft: current, history } = ensured;
      const nextHistory = pushDraftHistory(history, current);
      const nextTree = insertNodeIntoTree(current.locationHierarchy, parentId, node);
      return {
        locationDraftByProjectId: {
          ...s.locationDraftByProjectId,
          [projectId]: { ...current, locationHierarchy: nextTree },
        },
        locationDraftHistoryByProjectId: {
          ...s.locationDraftHistoryByProjectId,
          [projectId]: nextHistory,
        },
      };
    });
  },

  updateLocationDraftNode(
    projectId: string,
    nodeId: string,
    updates: Partial<Pick<LocationTreeNode, 'location_type' | 'name' | 'location_group' | 'meta'>>,
  ) {
    set((s) => {
      const ensured = ensureLocationDraftFromState(s, projectId);
      if (!ensured?.draft.locationHierarchy?.length) return s;
      const { draft: current, history } = ensured;
      const nextHistory = pushDraftHistory(history, current);
      const nextTree = updateNodeInTree(current.locationHierarchy, nodeId, updates);
      return {
        locationDraftByProjectId: {
          ...s.locationDraftByProjectId,
          [projectId]: { ...current, locationHierarchy: nextTree },
        },
        locationDraftHistoryByProjectId: {
          ...s.locationDraftHistoryByProjectId,
          [projectId]: nextHistory,
        },
      };
    });
  },

  updateLocationDraftNodesByName(
    projectId: string,
    name: string,
    updates: Partial<Pick<LocationTreeNode, 'location_type'>>,
  ) {
    set((s) => {
      const ensured = ensureLocationDraftFromState(s, projectId);
      if (!ensured?.draft.locationHierarchy?.length) return s;
      const { draft: current, history } = ensured;
      const nextHistory = pushDraftHistory(history, current);
      const nextTree = updateNodesInTreeByName(current.locationHierarchy, name, updates);
      return {
        locationDraftByProjectId: {
          ...s.locationDraftByProjectId,
          [projectId]: { ...current, locationHierarchy: nextTree },
        },
        locationDraftHistoryByProjectId: {
          ...s.locationDraftHistoryByProjectId,
          [projectId]: nextHistory,
        },
      };
    });
  },

  cloneLocationDraftNode(projectId: string, nodeId: string) {
    set((s) => {
      const ensured = ensureLocationDraftFromState(s, projectId);
      if (!ensured?.draft.locationHierarchy?.length) return s;
      const { draft: current, history, isNew } = ensured;
      const removedSet = new Set(current.removedIds);
      const effectiveTree = filterTreeRemoved(current.locationHierarchy, removedSet);
      const node = findNodeById(effectiveTree, nodeId);
      if (!node) return s;
      const nextHistory = isNew ? history : pushDraftHistory(history, current);
      const parentId = node.parent_location_id ?? null;
      const clone = buildCloneSubtree(node, parentId, true);
      if (isFloorLocationType(node.location_type)) {
        const parent =
          parentId != null ? findNodeById(effectiveTree, String(parentId)) : null;
        const siblings = parent ? (parent.child ?? []) : effectiveTree;
        const nextIndex = nextFloorIndexUnderParent(siblings);
        clone.name = `Floor ${nextIndex}`;
        clone.meta = withFloorNumberMeta(
          { ...(node.meta && typeof node.meta === 'object' ? node.meta : {}) },
          nextIndex,
        );
      }
      const nextTree = insertNodeIntoTree(current.locationHierarchy, parentId, clone);
      return {
        locationDraftByProjectId: {
          ...s.locationDraftByProjectId,
          [projectId]: { ...current, locationHierarchy: nextTree },
        },
        locationDraftHistoryByProjectId: {
          ...s.locationDraftHistoryByProjectId,
          [projectId]: nextHistory,
        },
      };
    });
  },

  pasteLocationDraftSubtree(
    projectId: string,
    parentId: string,
    sourceSubtree: LocationTreeNode,
  ): LocationTreeNode | null {
    const pasted = buildCloneSubtree(sourceSubtree, parentId, false);
    let didInsert = false;
    set((s) => {
      const ensured = ensureLocationDraftFromState(s, projectId);
      if (!ensured?.draft.locationHierarchy?.length) return s;
      const { draft: current, history, isNew } = ensured;
      const nextHistory = isNew ? history : pushDraftHistory(history, current);
      const nextTree = insertNodeIntoTree(current.locationHierarchy, parentId, pasted);
      didInsert = true;
      return {
        locationDraftByProjectId: {
          ...s.locationDraftByProjectId,
          [projectId]: { ...current, locationHierarchy: nextTree },
        },
        locationDraftHistoryByProjectId: {
          ...s.locationDraftHistoryByProjectId,
          [projectId]: nextHistory,
        },
      };
    });
    return didInsert ? pasted : null;
  },

  applyLocationDraftOps(projectId: string, ops: LocationDraftOp[]) {
    if (ops.length === 0) return;
    set((s) => {
      const ensured = ensureLocationDraftFromState(s, projectId);
      if (!ensured?.draft.locationHierarchy?.length) return s;
      const { draft: current, history } = ensured;
      const nextHistory = pushDraftHistory(history, current);

      let tree = current.locationHierarchy;
      const removed = new Set(current.removedIds.map((id) => String(id)));
      for (const op of ops) {
        if (op.kind === 'add') {
          tree = insertNodeIntoTree(tree, op.parentId, op.node);
        } else if (op.kind === 'update') {
          tree = updateNodeInTree(tree, op.nodeId, op.updates);
        } else if (op.kind === 'move') {
          tree = moveNodesInTree(tree, [op.nodeId], op.parentId, op.index);
        } else {
          op.ids.forEach((id) => removed.add(String(id)));
        }
      }

      return {
        locationDraftByProjectId: {
          ...s.locationDraftByProjectId,
          [projectId]: { locationHierarchy: tree, removedIds: Array.from(removed) },
        },
        locationDraftHistoryByProjectId: {
          ...s.locationDraftHistoryByProjectId,
          [projectId]: nextHistory,
        },
      };
    });
  },

  undoLocationDraftStep(projectId: string) {
    const { locationDraftByProjectId, locationDraftHistoryByProjectId } = get();
    const current = locationDraftByProjectId[projectId];
    const history = locationDraftHistoryByProjectId[projectId];
    if (!current || !history || history.past.length === 0) return;
    const prev = history.past[history.past.length - 1];
    const remainingPast = history.past.slice(0, -1);
    const futureHead: LocationDraftState = JSON.parse(JSON.stringify(current));
    const nextFuture = [futureHead, ...history.future].slice(0, 20);
    set((s) => ({
      locationDraftByProjectId: {
        ...s.locationDraftByProjectId,
        [projectId]: prev,
      },
      locationDraftHistoryByProjectId: {
        ...s.locationDraftHistoryByProjectId,
        [projectId]: { ...history, past: remainingPast, future: nextFuture },
      },
    }));
  },

  redoLocationDraftStep(projectId: string) {
    const { locationDraftByProjectId, locationDraftHistoryByProjectId } = get();
    const current = locationDraftByProjectId[projectId];
    const history = locationDraftHistoryByProjectId[projectId];
    if (!current || !history || history.future.length === 0) return;
    const next = history.future[0];
    const remainingFuture = history.future.slice(1);
    const pastSnapshot: LocationDraftState = JSON.parse(JSON.stringify(current));
    const nextPast = [...history.past, pastSnapshot].slice(-20);
    set((s) => ({
      locationDraftByProjectId: {
        ...s.locationDraftByProjectId,
        [projectId]: next,
      },
      locationDraftHistoryByProjectId: {
        ...s.locationDraftHistoryByProjectId,
        [projectId]: { ...history, past: nextPast, future: remainingFuture },
      },
    }));
  },

  resetLocationDraftToInitial(projectId: string) {
    const history = get().locationDraftHistoryByProjectId[projectId];
    if (!history?.initial) return;
    const initial: LocationDraftState = JSON.parse(JSON.stringify(history.initial));
    set((s) => ({
      locationDraftByProjectId: {
        ...s.locationDraftByProjectId,
        [projectId]: initial,
      },
      locationDraftHistoryByProjectId: {
        ...s.locationDraftHistoryByProjectId,
        [projectId]: { ...history, past: [], future: [] },
      },
    }));
  },
}));
